import { Link } from 'react-router-dom';
import { BedDouble, Bath, Maximize } from 'lucide-react';
import { schemeImage, slugify } from '../../lib/site';

export interface Listing {
  id: string;
  slug: string;
  headline: string;
  description?: string | null;
  advertisedRentMinor: number;
  advertisedRentCurrency: string;
  rentPeriod: string;
  depositMinor?: number | null;
  depositCurrency?: string;
  minTermMonths?: number;
  availableFrom: string | null;
  status: string;
  media?: string[];
  unit?: { bedrooms: number; bathrooms: number; areaSqft: number | null; unitType: string; isFurnished?: boolean; features?: string[] };
  property?: { name: string; slug?: string; city: string | null; region: string; postcode?: string | null; amenities?: string[] };
}

export function formatRent(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(minor / 100);
}

export const bedsLabel = (n?: number) => (n === 0 ? 'Studio' : n === 1 ? '1 bed' : `${n ?? '—'} bed`);

/** A home on the website. Photography comes from the scheme; the numbers come from the listing. */
export function ListingCard({ listing, index = 0, image: override }: { listing: Listing; index?: number; image?: string }) {
  const rent = formatRent(listing.advertisedRentMinor, listing.advertisedRentCurrency ?? 'GBP');
  const image = listing.media?.[0] ?? override ?? schemeImage(listing.property?.slug ?? slugify(listing.property?.name), index);
  const period = listing.rentPeriod === 'YEAR' ? 'pa' : 'pcm';
  return (
    <Link to={`/rent/${listing.slug}`} className="group block">
      <div className="relative aspect-[4/3] overflow-hidden rounded-[10px] bg-ink-100">
        <img src={image} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink-900/70 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/80">{listing.property?.city}</div>
            <div className="display mt-0.5 text-2xl">{listing.property?.name}</div>
          </div>
          <div className="rounded-full bg-white/95 px-3 py-1.5 text-[12px] font-semibold text-ink-900">{rent} {period}</div>
        </div>
      </div>
      <div className="mt-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-[15px] font-medium text-ink-900 group-hover:text-bronze-600">{listing.headline}</div>
          <div className="mt-1 flex items-center gap-3 text-xs text-ink-500">
            <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" />{bedsLabel(listing.unit?.bedrooms)}</span>
            <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" />{listing.unit?.bathrooms ?? 1} bath</span>
            {listing.unit?.areaSqft && <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" />{listing.unit.areaSqft} sq ft</span>}
          </div>
        </div>
        <div className="shrink-0 text-right text-[11px] text-ink-400">
          {listing.availableFrom && new Date(listing.availableFrom) > new Date()
            ? `From ${new Date(listing.availableFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
            : 'Available now'}
        </div>
      </div>
    </Link>
  );
}
